'use client';

import { BaseTranslations } from '../../../../baseTranslations';
import { useStickyFooterApplyClick } from '../../../../hooks/useStickyFooterApplyClick';
import { useCmsClientContext } from '../../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../../utils/translations/getClientTranslations';
import { tr } from '../../../../utils/translations/tr';
import { ApplyCtaLink, useCtaUrl } from '../../../ApplyCta';
import Button from '../../../Button';
import OpenInNew from '../../../icons/OpenInNew';
import { StickyFooterCtaBar } from '../../../StickyFooterCtaBar/StickyFooterCtaBar';
import { ProductDetailsProps } from '../../ProductDetails';

const PRODUCT_NAME_ANCHOR = ':product-name';

type ProductDetailsStickyFooterProps = ProductDetailsProps & {
  /** CTA label; the generic "Apply Now" when absent. TI passes its standardised label. */
  ctaLabel?: string;
};

/**
 * Sticky footer CTA (CMS-2043): "Apply for **<product name>**" and Apply Now through `ApplyCtaLink`
 * (with `ctaLinkOverride`, see `ProductDetailsProps`), firing the shared sticky-footer Apply event.
 * No apply URL, no footer — as with the hero CTA (CMS-1966).
 */
export const ProductDetailsStickyFooter: React.FC<
  ProductDetailsStickyFooterProps
> = ({ product, pushDataLayer, ctaLinkOverride, ctaLabel }) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const ctaUrl = useCtaUrl({ product, ctaLinkOverride });
  const handleApplyClick = useStickyFooterApplyClick({
    product,
    pushDataLayer,
  });

  if (!product?.product_name || !ctaUrl) {
    return null;
  }

  // The product name is bold inside the translated copy, so the copy is split at its anchor
  // rather than interpolated as a string.
  const [beforeName, afterName = ''] = tr('apply_for', staticText).split(
    PRODUCT_NAME_ANCHOR,
  );

  return (
    <StickyFooterCtaBar
      label={staticText.apply}
      heading={
        <>
          {beforeName}
          <strong>{product.product_name}</strong>
          {afterName}
        </>
      }
      cta={
        <ApplyCtaLink
          product={product}
          ctaLinkOverride={ctaLinkOverride}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-block"
        >
          <Button
            styleType="cta"
            endIcon={<OpenInNew className="size-full" />}
            onClick={handleApplyClick}
          >
            {ctaLabel ?? staticText.apply_now}
          </Button>
        </ApplyCtaLink>
      }
    />
  );
};
